import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, CreateUserPayload } from "@/api/users";
import { useRoles } from "@/api/roles";
import { useClients, displayName as clientDisplayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

/** Código del rol de sistema que habilita el acceso al Portal del cliente (Fase 19). */
const CLIENTE_ROLE_CODE = "CLIENTE";

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
  PENDING_VERIFICATION: "Pendiente de verificación",
  LOCKED: "Bloqueado",
};

export default function UsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useUsers({ search: search || undefined, page, pageSize: 10 });
  const { data: roles } = useRoles();
  const deleteUser = useDeleteUser();
  const updateUser = useUpdateUser();

  const users = data?.data ?? [];
  const meta = data?.meta;

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`¿Eliminar al usuario "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteUser.mutateAsync(id);
      toast.success("Usuario eliminado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateUser.mutateAsync({ id, status: nextStatus });
      toast.success("Estado actualizado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleCommissionBlur = async (id: string, currentRate: string | null, rawValue: string) => {
    const trimmed = rawValue.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next !== null && (Number.isNaN(next) || next < 0 || next > 100)) {
      toast.error("La comisión debe ser un número entre 0 y 100.");
      return;
    }
    const current = currentRate === null ? null : Number(currentRate);
    if (next === current) return;
    try {
      await updateUser.mutateAsync({ id, commissionRate: next });
      toast.success("Comisión actualizada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleGoalBlur = async (id: string, currentGoal: string | null, rawValue: string) => {
    const trimmed = rawValue.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next !== null && (Number.isNaN(next) || next < 0)) {
      toast.error("La meta debe ser un monto mayor o igual a cero.");
      return;
    }
    const current = currentGoal === null ? null : Number(currentGoal);
    if (next === current) return;
    try {
      await updateUser.mutateAsync({ id, monthlyGoal: next });
      toast.success("Meta mensual actualizada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Usuarios</h1>
        {hasPermission("users.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            + Nuevo usuario
          </button>
        )}
      </div>

      <div className="card p-4">
        <input
          type="search"
          placeholder="Buscar por nombre o correo..."
          className="input-field max-w-sm"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Correo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Roles</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500" title="Comisión sobre ventas del POS de tienda">
                Comisión %
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500" title="Meta de venta mensual del POS de tienda">
                Meta mensual
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No hay usuarios que coincidan con la búsqueda.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3 font-medium text-gray-900">
                  {u.fullName}
                  {u.clientId && (
                    <span
                      className="ml-2 inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
                      title="Usuario del Portal del cliente"
                    >
                      Portal
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3 text-gray-600">{u.roles.map((r) => r.name).join(", ")}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      u.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {STATUS_LABEL[u.status]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {hasPermission("users.update") ? (
                    <input
                      key={u.commissionRate ?? ""}
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      placeholder="—"
                      defaultValue={u.commissionRate ?? ""}
                      className="input-field w-20 py-1 text-xs"
                      onBlur={(e) => handleCommissionBlur(u.id, u.commissionRate, e.target.value)}
                    />
                  ) : (
                    <span className="text-gray-600">{u.commissionRate ?? "—"}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {hasPermission("users.update") ? (
                    <input
                      key={u.monthlyGoal ?? ""}
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="—"
                      defaultValue={u.monthlyGoal ?? ""}
                      className="input-field w-28 py-1 text-xs"
                      onBlur={(e) => handleGoalBlur(u.id, u.monthlyGoal, e.target.value)}
                    />
                  ) : (
                    <span className="text-gray-600">{u.monthlyGoal ?? "—"}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {hasPermission("users.update") && (
                    <button
                      type="button"
                      className="text-brand-600 hover:text-brand-800 text-xs font-medium"
                      onClick={() => handleToggleStatus(u.id, u.status)}
                    >
                      {u.status === "ACTIVE" ? "Desactivar" : "Activar"}
                    </button>
                  )}
                  {hasPermission("users.delete") && (
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                      onClick={() => handleDelete(u.id, u.fullName)}
                    >
                      Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} usuarios)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} availableRoles={roles ?? []} />}
    </div>
  );
}

function CreateUserModal({
  onClose,
  availableRoles,
}: {
  onClose: () => void;
  availableRoles: Array<{ id: string; name: string; code: string }>;
}) {
  const { register, handleSubmit, formState, watch } = useForm<CreateUserPayload>();
  const createUser = useCreateUser();

  const clienteRole = availableRoles.find((r) => r.code === CLIENTE_ROLE_CODE);
  const selectedRoleIds = watch("roleIds");
  const isPortalUser = !!clienteRole && (Array.isArray(selectedRoleIds) ? selectedRoleIds.includes(clienteRole.id) : selectedRoleIds === clienteRole.id);

  const { data: clientsData } = useClients({ pageSize: 100, status: "ACTIVE" });
  const clients = clientsData?.data ?? [];

  const onSubmit = async (values: CreateUserPayload) => {
    if (isPortalUser && !values.clientId) {
      toast.error("Debe seleccionar el cliente al que se vincula este usuario del portal.");
      return;
    }
    try {
      await createUser.mutateAsync({
        ...values,
        roleIds: Array.isArray(values.roleIds) ? values.roleIds : [values.roleIds].filter(Boolean),
        clientId: isPortalUser ? values.clientId : undefined,
      });
      toast.success("Usuario creado correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900">Nuevo usuario</h2>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre completo</label>
            <input className="input-field mt-1" {...register("fullName", { required: true })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Correo electrónico</label>
            <input type="email" className="input-field mt-1" {...register("email", { required: true })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contraseña temporal</label>
            <input type="password" className="input-field mt-1" {...register("password", { required: true, minLength: 10 })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Roles</label>
            <select multiple className="input-field mt-1 h-32" {...register("roleIds", { required: true })}>
              {availableRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">Mantenga presionado Ctrl/Cmd para seleccionar varios.</p>
          </div>

          {isPortalUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente vinculado (Portal del cliente)</label>
              <select className="input-field mt-1" {...register("clientId", { required: isPortalUser })}>
                <option value="">Seleccione un cliente...</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {clientDisplayName(c)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Este usuario tendrá el rol Cliente y solo podrá acceder al Portal del cliente, con acceso restringido a la
                información de este cliente.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={formState.isSubmitting || createUser.isPending}>
              {createUser.isPending ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
